import { api } from '@data/store';
import { OrganizationEntity } from '@data';
import type {
  Chapter,
  CreateChapterValues,
  OrganizationSlackAuthorizeValues,
  UpdateChapterValues,
} from '@data/utils/types';

export const fetchOrganization = (organizationId: string): Promise<OrganizationEntity> =>
  organizationId && api.get(`/organizations/${organizationId}`);

export const fetchOrganizationChapters = (
  organizationId: string,
  options?: { includeDisabled?: boolean },
): Promise<{ items: Chapter[]; count: number }> =>
  api.get(`/organizations/${organizationId}/chapters`, {
    params: options?.includeDisabled ? { includeDisabled: 'true' } : undefined,
  });

// silentStatuses on the create/delete calls: 409 is the documented happy-path branch (duplicate key
// surfaces inline on the form; in-use chapter flips the delete dialog to "Disable instead"), so we
// suppress the global error toast — the local onError handler renders the user-facing UI for it.
export const createChapter = (organizationId: string, values: CreateChapterValues): Promise<Chapter> =>
  api.post(`/organizations/${organizationId}/chapters`, values, { silentStatuses: [409] });

export const updateChapter = (
  organizationId: string,
  chapterId: string,
  values: UpdateChapterValues,
): Promise<Chapter> => api.patch(`/organizations/${organizationId}/chapters/${chapterId}`, values);

/** Every chapter's id, disabled ones included, top of the ranking first. */
export const reorderChapters = (
  organizationId: string,
  chapterIds: string[],
): Promise<{ items: Chapter[]; count: number }> =>
  api.put(`/organizations/${organizationId}/chapters/order`, { chapterIds });

export const deleteChapter = (organizationId: string, chapterId: string): Promise<void> =>
  api.delete(`/organizations/${organizationId}/chapters/${chapterId}`, { silentStatuses: [409] });

export const authorizeSlack = (
  organizationId: string,
  values: OrganizationSlackAuthorizeValues,
): Promise<OrganizationEntity> => api.post(`organizations/${organizationId}/slack/authorize`, values);

export const updateOrganization = (
  organizationId: string,
  values: Partial<OrganizationEntity>,
): Promise<OrganizationEntity> => api.patch(`/organizations/${organizationId}`, values);

export const revokeSlack = (organizationId: string): Promise<OrganizationEntity> =>
  api.post(`organizations/${organizationId}/slack/revoke`);

// The real end-of-day nudge, to you alone, instead of waiting for the scheduled run. Nudging the whole
// organization is an admin action on a separate route.
export const sendReportNudgeToSelf = (organizationId: string): Promise<void> =>
  api.post(`/organizations/${organizationId}/slack/report-nudge/me`);
